import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js';
import MuiTablePagination from '../../../uiComponents/MuiTable/MuiTablePagination';
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader.js';
import { stableSort, getComparator, handleRequestSort, handleChangePage, handleChangeRowsPerPage } from '../../../uiComponents/MuiTable/MuiHelper';
import {
  TableBody,
  TableContainer,
  Box,
} from '@mui/material';

// Preserves the table's pagination/sort so it can be restored when the user
// navigates to variant details and returns via the browser back button.
const TABLE_STATE_KEY = 'exploreGenotypePatientsTable';

const getSavedTableState = () => {
  try {
    return JSON.parse(sessionStorage.getItem(TABLE_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

const GenotypePatientsTable = ({ data, noResultsMessage, fields }) => {
  const savedTableState = getSavedTableState();
  const [order, setOrder] = useState(savedTableState.order || 'asc');
  const [orderBy, setOrderBy] = useState(savedTableState.orderBy || 'patientId');
  const [page, setPage] = useState(Number.isInteger(savedTableState.page) ? savedTableState.page : 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number.isInteger(savedTableState.rowsPerPage) ? savedTableState.rowsPerPage : 10
  );

  useEffect(() => {
    // Persist pagination/sort whenever it changes so it survives back navigation
    sessionStorage.setItem(
      TABLE_STATE_KEY,
      JSON.stringify({ order, orderBy, page, rowsPerPage })
    );
  }, [order, orderBy, page, rowsPerPage]);

  // Patient ID is always the required first column; the remaining columns are
  // driven by the (ordered) active fields config.
  const activeFields = Array.isArray(fields) ? fields : [];

  const headCells = [
    { label: 'Patient ID', id: 'patientId', numeric: false, disablePadding: false },
    ...activeFields.map((field) => ({
      label: field.label,
      id: field.id,
      numeric: false,
      disablePadding: false,
    })),
  ];

  // Resolve the display value for a field from the raw row. Analyte values are
  // keyed by code in analyteData; clinical-diagnosis values come from the
  // matching clinicalDiagnoses entry's finalDiagnosis. Missing data renders as
  // an empty cell.
  const getFieldValue = (row, field) => {
    if (field.type === 'analyte') {
      const value = row.analyteData ? row.analyteData[field.id] : undefined;
      return value === undefined || value === null ? '' : value;
    }
    if (field.type === 'condition') {
      const match = Array.isArray(row.clinicalDiagnoses)
        ? row.clinicalDiagnoses.find((diagnosis) => diagnosis.condition === field.id)
        : undefined;
      return match && match.finalDiagnosis ? match.finalDiagnosis : '';
    }
    return row[field.id];
  };

  // Flatten analyte/condition values onto each row keyed by field id so sorting
  // and rendering can read them uniformly with the demographic columns.
  const displayData = (Array.isArray(data) ? data : []).map((row) => {
    const derived = { ...row };
    activeFields.forEach((field) => {
      if (field.type === 'analyte' || field.type === 'condition') {
        derived[field.id] = getFieldValue(row, field);
      }
    });
    return derived;
  });

  if (!data || data.length === 0) {
    return (
      <TableContainer component={StyledPaper}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {noResultsMessage}
        </Box>
      </TableContainer>
    );
  }

  return (
    <>
      <TableContainer component={StyledPaper}>
        <StyledTable
          size='small'
          aria-label="genotype search results table"
          key={JSON.stringify(data)}
        >
          <MuiTableHeader
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={(event, property) => handleRequestSort(event, property, order, orderBy, setOrder, setOrderBy)}
          />
          <TableBody>
            {stableSort(displayData, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>{row.patientId}</StyledTableCell>
                  {activeFields.map((field) => (
                    <StyledTableCell key={field.id}>{row[field.id]}</StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <MuiTablePagination
        rowsPerPage={rowsPerPage}
        page={page}
        count={data.length}
        onPageChange={(event, newPage) => handleChangePage(event, newPage, setPage)}
        onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setRowsPerPage, setPage)}
      />
    </>
  );
};

GenotypePatientsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      patientId: PropTypes.string,
      patientRace: PropTypes.string,
      patientEthnicity: PropTypes.string,
      patientSex: PropTypes.string,
      birthYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  noResultsMessage: PropTypes.string,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
};

GenotypePatientsTable.defaultProps = {
  noResultsMessage: 'No Search Results',
  fields: [],
};

export default GenotypePatientsTable;
